package br.com.heyagenda.data.remote

import br.com.heyagenda.domain.model.SavedClient
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.DocumentSnapshot
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map

interface FirestoreClientDataSource {
    fun observeClients(uid: String): Flow<List<SavedClient>>
    suspend fun getClients(uid: String): List<SavedClient>
    suspend fun saveClient(uid: String, client: SavedClient): Result<Unit>
    suspend fun deleteClient(uid: String, clientId: String): Result<Unit>
}

class FirestoreClientDataSourceImpl : FirestoreClientDataSource {

    private val firestore: FirebaseFirestore? by lazy {
        try {
            Firebase.firestore
        } catch (e: Throwable) {
            println(">>> [FirestoreClient ERROR] Failed to get Firebase.firestore: ${e.message}")
            null
        }
    }

    override suspend fun getClients(uid: String): List<SavedClient> {
        val fs = firestore ?: return emptyList()
        return try {
            val snapshot = fs.collection("professionals")
                .document(uid)
                .collection("clients")
                .get()
            snapshot.documents.mapNotNull { parseSavedClient(it) }
        } catch (e: Throwable) {
            println(">>> [FirestoreClient GET ERROR] $uid: ${e.message}")
            emptyList()
        }
    }

    override fun observeClients(uid: String): Flow<List<SavedClient>> = flow {
        val fs = firestore
        if (fs == null) {
            emit(emptyList())
            return@flow
        }

        val direct = getClients(uid)
        emit(direct)

        try {
            fs.collection("professionals")
                .document(uid)
                .collection("clients")
                .snapshots
                .map { snapshot ->
                    snapshot.documents.mapNotNull { parseSavedClient(it) }
                }
                .catch { e ->
                    println(">>> [FirestoreClient SNAPSHOT ERROR] $uid: ${e.message}")
                }
                .collect { list ->
                    emit(list)
                }
        } catch (e: Throwable) {
            println(">>> [FirestoreClient SNAPSHOT SETUP ERROR] ${e.message}")
        }
    }

    override suspend fun saveClient(uid: String, client: SavedClient): Result<Unit> {
        val fs = firestore ?: return Result.failure(IllegalStateException("Firestore unavailable"))
        val cleanName = client.name.trim()
        if (cleanName.isBlank()) return Result.success(Unit)

        val rawPhone = client.phone.orEmpty()
        val digits = rawPhone.filter { it.isDigit() }.let {
            if (it.length > 11 && it.startsWith("55")) it.drop(2) else it
        }.take(11)
        val formattedPhone = if (digits.isNotBlank()) {
            br.com.heyagenda.domain.util.PhoneUtils.formatDigits(digits)
        } else {
            rawPhone.trim()
        }

        return try {
            val clientsCollection = fs.collection("professionals")
                .document(uid)
                .collection("clients")

            // 1. Resolve document reference:
            // - If client.id is present, use it directly (editing existing).
            // - Otherwise, query existing by phone digits or fallback to deterministic ID "p_$digits" / "n_$name"
            val targetDoc = if (client.id.isNotBlank()) {
                clientsCollection.document(client.id)
            } else if (digits.isNotBlank()) {
                val existingClients = try { getClients(uid) } catch (_: Throwable) { emptyList() }
                val match = existingClients.find { existing ->
                    val existingDigits = existing.phone.orEmpty().filter { it.isDigit() }.let {
                        if (it.length > 11 && it.startsWith("55")) it.drop(2) else it
                    }.take(11)
                    existingDigits.isNotBlank() && existingDigits == digits
                }
                if (match != null && match.id.isNotBlank()) {
                    clientsCollection.document(match.id)
                } else {
                    clientsCollection.document("p_$digits")
                }
            } else {
                clientsCollection.document("n_" + cleanName.lowercase().replace(" ", "_"))
            }

            val existingDoc = try { targetDoc.get() } catch (_: Throwable) { null }
            val existingTotal = if (existingDoc != null && existingDoc.exists) {
                try { existingDoc.get<Int?>("totalAppointments") ?: 1 } catch (_: Throwable) { 1 }
            } else 0

            val total = if (client.id.isNotBlank()) {
                // Editing from management UI preserves explicit appointment count
                client.totalAppointments
            } else if (existingTotal > 0) {
                existingTotal + 1
            } else {
                client.totalAppointments.coerceAtLeast(1)
            }

            val existingNotes = if (existingDoc != null && existingDoc.exists) {
                try { existingDoc.get<String?>("notes") } catch (_: Throwable) { null }
            } else null
            val finalNotes = client.notes ?: existingNotes

            val dataMap = mutableMapOf<String, Any>(
                "id" to targetDoc.id,
                "name" to cleanName,
                "phone" to formattedPhone,
                "totalAppointments" to total,
                "lastAppointmentDate" to (client.lastAppointmentDate ?: ""),
                "lastServiceName" to (client.lastServiceName ?: "")
            )
            if (!finalNotes.isNullOrBlank()) {
                dataMap["notes"] = finalNotes
            }

            targetDoc.set(
                dataMap,
                merge = true
            )
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [FirestoreClient SAVE ERROR] $uid: ${e.message}")
            Result.failure(e)
        }
    }

    override suspend fun deleteClient(uid: String, clientId: String): Result<Unit> {
        val fs = firestore ?: return Result.failure(IllegalStateException("Firestore unavailable"))
        if (clientId.isBlank()) return Result.success(Unit)
        return try {
            fs.collection("professionals")
                .document(uid)
                .collection("clients")
                .document(clientId)
                .delete()
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [FirestoreClient DELETE ERROR] $uid / $clientId: ${e.message}")
            Result.failure(e)
        }
    }

    private fun parseSavedClient(doc: DocumentSnapshot): SavedClient? {
        return try {
            val name: String = try { doc.get<String?>("name") ?: "" } catch (_: Throwable) { "" }
            if (name.isBlank()) return null

            val phone: String? = try { doc.get<String?>("phone")?.takeIf { it.isNotBlank() } } catch (_: Throwable) { null }
            val total: Int = try { doc.get<Int?>("totalAppointments") ?: 1 } catch (_: Throwable) { 1 }
            val lastDate: String? = try { doc.get<String?>("lastAppointmentDate")?.takeIf { it.isNotBlank() } } catch (_: Throwable) { null }
            val lastService: String? = try { doc.get<String?>("lastServiceName")?.takeIf { it.isNotBlank() } } catch (_: Throwable) { null }
            val notes: String? = try { doc.get<String?>("notes")?.takeIf { it.isNotBlank() } } catch (_: Throwable) { null }

            SavedClient(
                id = doc.id,
                name = name,
                phone = phone,
                totalAppointments = total,
                lastAppointmentDate = lastDate,
                lastServiceName = lastService,
                notes = notes
            )
        } catch (e: Throwable) {
            null
        }
    }
}
